import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useReportMeasuresGroupByOptions } from "hooks";

// Row by options (for Last Group) - same as ReportSetTableGroupBy
const rowByOptions = [
  { text: "Deal", key: "deal", value: "deal" },
  { text: "Investor", key: "investor", value: "investor" },
  { text: "Measure", key: "measure", value: "measure" }
] as const;

type RowByValue = "deal" | "investor" | "measure";

export interface TableGroupByResult {
  groupBy: [string, string];
  rowBy: RowByValue;
}

interface TableGroupBySectionProps {
  groupBy: [string, string];
  rowBy: RowByValue;
  onSubmit: (result: TableGroupByResult) => void;
  onCancel: () => void;
}

export const TableGroupBySection: React.FC<TableGroupBySectionProps> = ({
  groupBy: initialGroupBy,
  rowBy: initialRowBy,
  onSubmit,
  onCancel
}) => {
  // Local state for editing
  const [groupBy, setGroupBy] = useState<[string, string]>(initialGroupBy);
  const [rowBy, setRowBy] = useState<RowByValue>(initialRowBy);

  const groupByOptions = useReportMeasuresGroupByOptions();

  // Filter options to exclude already selected values
  const getGroupOptions = (currentIndex: number) => {
    const otherValue = currentIndex === 0 ? groupBy[1] : groupBy[0];
    return groupByOptions.filter((opt) => opt.value !== otherValue);
  };

  const handleGroupChange = (index: 0 | 1, value: string) => {
    const newGroupBy: [string, string] = [...groupBy] as [string, string];
    newGroupBy[index] = value;
    setGroupBy(newGroupBy);
  };

  const handleSubmit = () => {
    onSubmit({ groupBy, rowBy });
  };

  return (
    <Stack sx={{ minWidth: "520px", borderRadius: "8px", boxShadow: "sm", padding: "16px", gap: "16px" }}>
      {/* Header */}
      <Stack
        sx={{
          p: "10px 0px",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #dae0e2"
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 500, lineHeight: "24px", whiteSpace: "nowrap" }}>
          Group by
        </Typography>
        <IconButton size="sm" onClick={onCancel}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
      {/* Content */}
      <Stack sx={{ gap: "16px" }}>
        {/* Group 1 */}
        <Autocomplete
          size="small"
          options={getGroupOptions(0)}
          value={groupByOptions.find((opt) => opt.value === groupBy[0]) || groupByOptions[0]}
          onChange={(_, newValue) => handleGroupChange(0, String(newValue?.value ?? "none"))}
          onInputChange={(_, __, reason) => {
            if (reason === "clear") handleGroupChange(0, "none");
          }}
          getOptionLabel={(option) => option.text}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disablePortal={false}
          slotProps={{ listbox: { sx: { maxHeight: 300 } } }}
          renderInput={(params) => <TextField {...params} label="Group 1" />}
        />
        {/* Group 2 */}
        <Autocomplete
          size="small"
          disableClearable={groupBy[1] === "none"}
          options={getGroupOptions(1)}
          value={groupByOptions.find((opt) => opt.value === groupBy[1]) || groupByOptions[0]}
          onChange={(_, newValue) => handleGroupChange(1, String(newValue?.value ?? "none"))}
          onInputChange={(_, __, reason) => {
            if (reason === "clear") handleGroupChange(1, "none");
          }}
          getOptionLabel={(option) => option.text}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disablePortal={false}
          slotProps={{ listbox: { sx: { maxHeight: 300 } } }}
          renderInput={(params) => <TextField {...params} label="Group 2" />}
        />
        {/* Last Group (rowBy) - always deal/investor/measure */}
        <Autocomplete
          size="small"
          disableClearable
          options={[...rowByOptions]}
          value={rowByOptions.find((opt) => opt.value === rowBy) || rowByOptions[0]}
          onChange={(_, newValue) => setRowBy((newValue?.value as RowByValue) ?? "deal")}
          getOptionLabel={(option) => option.text}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disablePortal={false}
          slotProps={{ listbox: { sx: { maxHeight: 300 } } }}
          renderInput={(params) => <TextField {...params} label="Last Group" />}
        />
      </Stack>
      {/* Footer */}
      <Stack
        sx={{
          flexDirection: "row",
          gap: "8px",
          justifyContent: "flex-end",
          p: "12px 20px"
        }}
      >
        <Button variant="outlined" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" size="sm" onClick={handleSubmit}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};
